import {
  ConnectionState,
  LocalTrack,
  Room,
  RoomEvent,
  RoomOptions,
  Track,
} from "livekit-client";
import { ref, shallowRef } from "vue";
import {
  cameraTrack,
  microTrack,
  microTrackForProcessing,
  realCameraDevice,
  realMicroDevice,
} from "./state";
import { isCurrentSession, needSync } from "./pipe";
import { audioDevices } from "./devices";
import { mediaActions } from "./actions";
import { destroyWorkletNode } from "./audioProcessor";
import {
  ensureAudioContextRunning,
  microphoneAudioContext,
  resumeAudioContext,
} from "../audioCtx";
import type { AudioProcessor } from "../../utils/AudioProcessor";
import { authState } from "../auth";
import { localScreenTrack, removeSharedByApi, selfScreenShare } from "../screenShareState";
import { log } from "../../utils/logger";
import type { MediaDesired } from "./state";

const livekitRoom = shallowRef<Room | null>(null);

const livekitPublishReady = ref(false);

export function createLivekitRoomInstance(init: RoomOptions) {
  const room = new Room(init);
  livekitRoom.value = room;
  // После переподключения комнаты заново сводим публикации с желаемым
  // состоянием (silent-потеря публикации без этого не лечится до next needSync).
  room.on(RoomEvent.Reconnected, () => needSync());
  return room;
}

export async function livekitTask(desired: MediaDesired) {
  if (!isCurrentSession(desired.generation)) return;
  const room = livekitRoom.value;
  if (!room) return;
  resumeAudioContext();
  if (desired.audioDevice) {
    const currentDevice = room.getActiveDevice("audiooutput");
    const next = audioDevices.find(
      (d) => d.deviceId === desired.audioDevice,
    );
    if (currentDevice !== desired.audioDevice && next) {
      await room
        .switchActiveDevice("audiooutput", next.deviceId)
        .catch((err) => {
          console.log("switch audio error", err);
        });
    } else if (!next) {
      mediaActions.resetAudioDevice();
    }
  }
  if (room.state !== ConnectionState.Connected || !livekitPublishReady.value) {
    return;
  }
  // Камера и микрофон независимы — синхронизируем публикации параллельно.
  await Promise.all([
    syncCameraPublications(room, desired),
    syncMicroPublications(room, desired),
  ]);
}

/**
 * Снять локальные публикации заданного kind/source, не совпадающие с keepSid
 * (осиротевшие после пересоздания трека/смены устройства). keepSid опущен —
 * снимаются все (желаемое выключено). Экранная демонстрация имеет другие
 * kind/source и не затрагивается.
 */
async function unpublishOthers(
  room: Room,
  kind: Track.Kind,
  source: Track.Source,
  keepSid?: string,
) {
  const publications = room.localParticipant
    .getTrackPublications()
    .filter(
      (p) =>
        p.kind === kind &&
        p.source === source &&
        p.trackSid !== keepSid &&
        p.track,
    );
  for (const p of publications) {
    p.track!.stop();
    log.debug('unpublish', kind, source)
    await room.localParticipant
      .unpublishTrack(p.track as LocalTrack)
      .catch(() => {
        console.log("err unpublish", kind, source);
        needSync();
      });
  }
}

async function syncCameraPublications(room: Room, desired: MediaDesired) {
  const track = cameraTrack.value;
  if (desired.cameraEnabled && track) {
    // Снимаем осиротевшие публикации камеры (смена устройства/пересоздание),
    // текущий трек не трогаем.
    await unpublishOthers(room, Track.Kind.Video, Track.Source.Camera, track.sid);
    if (!isCurrentSession(desired.generation)) return;
    const published = room.localParticipant
      .getTrackPublications()
      .some((t) => t.trackSid === track.sid);
    if (!published) {
      const trackToPublish = track;
      if (!isCurrentSession(desired.generation)) return;
      log.debug('publish camera')
      await room.localParticipant.publishTrack(trackToPublish).catch((e) => {
        log.error('err publish camera', e)
        // Почему то livekit стопает трек в некоторых случаях
        trackToPublish.stop();
        if (cameraTrack.value === trackToPublish) {
          cameraTrack.value = null;
          realCameraDevice.value = null;
        }
        needSync();
      });
    }
  } else {
    // желаемое состояние камеры — выкл: снимаем все публикации камеры
    await unpublishOthers(room, Track.Kind.Video, Track.Source.Camera);
  }
}

async function syncMicroPublications(room: Room, desired: MediaDesired) {
  const track = microTrack.value;
  if (desired.microEnabled && track) {
    await unpublishOthers(room, Track.Kind.Audio, Track.Source.Microphone, track.sid);
    if (!isCurrentSession(desired.generation)) return;
    const published = room.localParticipant
      .getTrackPublications()
      .some((t) => t.trackSid === track.sid);
    if (!published) {
      const trackToPublish = track;
      const processingTrack = microTrackForProcessing.value;
      if (!isCurrentSession(desired.generation)) return;
      log.debug('publish micro')
      await ensureAudioContextRunning(microphoneAudioContext);
      if (!isCurrentSession(desired.generation)) return;
      await room.localParticipant.publishTrack(trackToPublish).catch((e) => {
        log.error('err publish micro', e)
        // Почему то livekit стопает трек в некоторых случаях
        trackToPublish.stop();
        if (microTrack.value === trackToPublish) {
          microTrack.value = null;
          realMicroDevice.value = null;
        }
        processingTrack?.stop();
        if (
          microTrackForProcessing.value === processingTrack &&
          processingTrack
        ) {
          microTrackForProcessing.value = null;
          destroyWorkletNode(processingTrack);
        }
        needSync();
      });
    }
  } else {
    // Желаемое состояние микрофона — выкл: текущий трек не анпаблишим
    // (правило mute вместо unpublish), снимаем только осиротевшие публикации.
    await unpublishOthers(
      room,
      Track.Kind.Audio,
      Track.Source.Microphone,
      microTrack.value?.sid,
    );
  }
}

export function setLivekitPublishReady(val: boolean) {
  livekitPublishReady.value = val;
  needSync();
}

export async function cleanupLivekit() {
  // Новая сессия сама выставит publish-ready при подключении к комнате.
  livekitPublishReady.value = false;
  const room = livekitRoom.value;
  if (!room) return;
  await room.localParticipant.setMicrophoneEnabled(false).catch(console.log);
  await room.localParticipant.setCameraEnabled(false).catch(console.log);
  if(room.localParticipant.isScreenShareEnabled) {
    room.localParticipant.setScreenShareEnabled(false);
  }
  selfScreenShare.value = false;
  localScreenTrack.value = null;
  removeSharedByApi(authState.id);

  const pubs = room.localParticipant.getTrackPublications();
  for (const p of pubs) {
    if (p.track) {
      const track = p.track as LocalTrack;
      const processor = track.getProcessor() as AudioProcessor | null;
      if (processor) {
        await processor.destroy().catch(console.log);
      }
      track.stop();
      await room.localParticipant.unpublishTrack(track).catch(() => { });
    }
  }

  await room.disconnect(true).catch(console.log);
  // Пока отключали комнату, могла создаться новая — её ссылку не затираем.
  if (livekitRoom.value === room) {
    livekitRoom.value = null;
  }
}

export function getLivekitRoomInstance() {
  return livekitRoom.value;
}
